import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, cancel, delay, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import { PS20Logger } from '@ps20-frontend/logging';
import {
  determineTkvV2Produkttabelle,
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapVorbelegungdaten,
  TierartEnum,
  TkvV2Vorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import {
  Produktlinienname,
  SpartenType,
  TaasTarifrisikoschluesselTKV_V2,
  TkvVorvertragsdaten,
  VertragsdatenRequestModel
} from '@ps20/bff-models';
import { AxiosError } from 'axios';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { TkvAction, TkvActions } from '../tkvActions';
import { IAppState } from '../appState';
import { convertFromBffTierart } from '../reducers/versicherungsnehmer.reducer';
import { CoverageType } from '../models/vertragsoptionen.model';
import {
  buildProduktReceivedMatcher,
  loadAndUpdateProduct,
  PRODUKT_RECEIVED_TIMEOUT_MS,
} from './productRelevantDataChangedSaga';

/**
 * Product line order from lowest to highest: BASIS < SMART < KOMFORT < PREMIUM
 */
const PRODUCT_LINE_ORDER: Produktlinienname[] = [
  Produktlinienname.BASIS,
  Produktlinienname.SMART,
  Produktlinienname.KOMFORT,
  Produktlinienname.PREMIUM,
];

/**
 * Calculates the next higher product line for Ersatzgeschäft with Heilbehandlung.
 * If previous line was PREMIUM, stays at PREMIUM.
 */
export function getNextHigherProductLine(currentLine: Produktlinienname): Produktlinienname {
  const currentIndex = PRODUCT_LINE_ORDER.indexOf(currentLine);
  if (currentIndex === -1) {
    return Produktlinienname.SMART; // Default to SMART if unknown
  }
  const nextIndex = Math.min(currentIndex + 1, PRODUCT_LINE_ORDER.length - 1);
  return PRODUCT_LINE_ORDER[nextIndex];
}



export function* waitForBreedListIfNeeded(
  animalType: TierartEnum,
): SagaIterator {
  const state = (yield select()) as IAppState;
  const versicherungsnehmer = state?.versicherungsnehmer;

  switch (animalType) {
    case TierartEnum.PFERD:
      if (!versicherungsnehmer?.horseBreeds?.length) {
        yield take(GlobalActions.PFERDERASSEN_LISTE_RETRIEVED);
      }
      break;
    case TierartEnum.HUND:
      if (!versicherungsnehmer?.dogBreeds?.length) {
        yield take(GlobalActions.HUNDERASSENLISTE_RETRIEVED);
      }
      break;
    case TierartEnum.KATZE:
      if (!versicherungsnehmer?.catBreeds?.length) {
        yield take(GlobalActions.KATZENRASSEN_LISTE_RETRIEVED);
      }
      break;
  }
}

export enum VorbelegungTkvRequestEventsEnum {
  VORBELEGUNG_TKV_V2_REQUEST_SUCCESSFUL = 'VORBELEGUNG_TKV_V2_REQUEST_SUCCESSFUL',
  VORBELEGUNG_TKV_V2_REQUEST_FAILED = 'VORBELEGUNG_TKV_V2_REQUEST_FAILED',
}

export function* watchVorbelegungRequested(): SagaIterator {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSaga, App.TKV_V2));
  yield takeEvery(
    VorbelegungTkvRequestEventsEnum.VORBELEGUNG_TKV_V2_REQUEST_SUCCESSFUL,
    safeSaga(successCallback, App.TKV_V2),
  );
  yield takeEvery(
    VorbelegungTkvRequestEventsEnum.VORBELEGUNG_TKV_V2_REQUEST_FAILED,
    safeSaga(errorCallback, App.TKV_V2),
  );
}

const globalActions = new GlobalActions();

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>): SagaIterator {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.TKV_V2 || action.payload.angebotsId !== state.angebotsId) {
    return;
  }
  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* tkvDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten, action.payload.angebotsId);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.TKV_V2,
        angebotsId: action.payload.angebotsId,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungTkvRequestEventsEnum.VORBELEGUNG_TKV_V2_REQUEST_SUCCESSFUL,
        errorEvent: VorbelegungTkvRequestEventsEnum.VORBELEGUNG_TKV_V2_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<TkvVorvertragsdaten>): SagaIterator {
  const state: IAppState = yield select();

  if (state.angebotsId !== successAction.doRequestAction.payload.angebotsId) {
    return yield cancel();
  }
  const tkvVorbelegungdaten: TkvV2Vorbelegungdaten = mapVorbelegungdaten(
    SpartenType.TIERKRANKEN_V2,
    successAction.payload,
  ) as TkvV2Vorbelegungdaten;
  yield* tkvDatenVorbelegen(tkvVorbelegungdaten, successAction.doRequestAction.payload.angebotsId);
}

const tkvActions = new TkvActions();

function* prefillProdukt(
  state: IAppState,
  tkvVorbelegungdaten: TkvV2Vorbelegungdaten,
  angebotsId: string,
): SagaIterator<boolean> {
  if (!state.vertragsoptionen?.beibehalten) {
    const hasHeilbehandlung = tkvVorbelegungdaten.vorvertragBausteinList.some(
      elem => elem.taasTarifrisikoschluessel === TaasTarifrisikoschluesselTKV_V2.HB);
    const coverageType = hasHeilbehandlung ? CoverageType.VOLLSCHUTZ : CoverageType.OPSCHUTZ;

    const animalType = convertFromBffTierart(tkvVorbelegungdaten.tierart);
    if (!animalType) {
      PS20Logger.error(
        App.TKV_V2,
        `Ersatzgeschaeft: unbekannte tierart=${tkvVorbelegungdaten.tierart} angebotsId=${angebotsId}`,
      );
      return false;
    }
    PS20Logger.info(
      'TKV_V2.Ersatzgeschaeft.vorbelegung',
      `angebotsId=${angebotsId} tierart=${tkvVorbelegungdaten.tierart} animalType=${animalType} coverageType=${coverageType}`,
      App.TKV_V2,
    );

    yield put(tkvActions.animalTypePrefilled(animalType));

    const animalTypeAction: TkvAction<TierartEnum> = {
      type: TkvActions.ANIMAL_TYPE_CHANGED,
      payload: animalType,
    };

    yield* loadAndUpdateProduct(animalTypeAction, animalType, coverageType);
    PS20Logger.info(
      'TKV_V2.Ersatzgeschaeft.produktLoaded',
      `angebotsId=${angebotsId} animalType=${animalType} coverageType=${coverageType}`,
      App.TKV_V2,
    );
  } else if (state.produkt?.produktlinien.length === 0) {
    const beibehaltenAnimalType = convertFromBffTierart(tkvVorbelegungdaten.tierart);
    if (!beibehaltenAnimalType) {
      PS20Logger.error(
        App.TKV_V2,
        'TKV_V2.vorbelegung.produktlinienEmpty.unknownTierart',
        `angebotsId=${angebotsId} tierart=${tkvVorbelegungdaten.tierart}`,
      );
      return false;
    }
    const beibehaltenHasHeilbehandlung = tkvVorbelegungdaten.vorvertragBausteinList.some(
      elem => elem.taasTarifrisikoschluessel === TaasTarifrisikoschluesselTKV_V2.HB,
    );
    const beibehaltenCoverageType = beibehaltenHasHeilbehandlung
      ? CoverageType.VOLLSCHUTZ
      : CoverageType.OPSCHUTZ;
    const expectedProdukttabelle = determineTkvV2Produkttabelle(
      beibehaltenAnimalType,
      beibehaltenCoverageType === CoverageType.VOLLSCHUTZ,
    );
    const matcher = buildProduktReceivedMatcher(
      App.TKV_V2,
      state.angebotsId,
      expectedProdukttabelle,
    );
    const { timeout } = (yield race({
      produkt: take(matcher),
      timeout: delay(PRODUKT_RECEIVED_TIMEOUT_MS),
    })) as { produkt?: AnyAction; timeout?: true };
    if (timeout) {
      PS20Logger.error(
        App.TKV_V2,
        'TKV_V2.vorbelegung.produktlinienEmpty.timeout',
        `angebotsId=${state.angebotsId} expected=${expectedProdukttabelle} animalType=${beibehaltenAnimalType} coverageType=${beibehaltenCoverageType}`,
      );
    }
  }
  return true;
}

function* finishVorbelegung(
  state: IAppState,
  tkvVorbelegungdaten: TkvV2Vorbelegungdaten,
  angebotsId: string,
): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  if (!state.vertragsoptionen?.beibehalten) {
    if (tkvVorbelegungdaten.produktlinienname) {
      if (tkvVorbelegungdaten.hasHeilbehandlungen) {
        const targetLine = getNextHigherProductLine(tkvVorbelegungdaten.produktlinienname);
        PS20Logger.info(
          'TKV_V2.Ersatzgeschaeft.produktlinie',
          `angebotsId=${angebotsId} upgraded ${tkvVorbelegungdaten.produktlinienname} → ${targetLine} (hasHeilbehandlungen=true)`,
          App.TKV_V2,
        );
        yield put(tkvActions.produktliniePrefilled(targetLine));
      } else {
        PS20Logger.info(
          'TKV_V2.Ersatzgeschaeft.produktlinie',
          `angebotsId=${angebotsId} kept ${tkvVorbelegungdaten.produktlinienname} (hasHeilbehandlungen=false)`,
          App.TKV_V2,
        );
        yield put(tkvActions.produktliniePrefilled(tkvVorbelegungdaten.produktlinienname));
      }
    }

    const animalType = convertFromBffTierart(tkvVorbelegungdaten.tierart);
    yield* waitForBreedListIfNeeded(animalType);

    yield put(tkvActions.vorbelegungForTkvRequested(tkvVorbelegungdaten));

    yield call(
      [globalPut, globalPut.put],
      globalActions.navigationRequested({
        appId: App.TKV_V2,
        angebotsId,
        subroute: '/eingabe',
      }),
    );
  } else {
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(tkvVorbelegungdaten),
    );
  }
}

export function* tkvDatenVorbelegen(
  vorbelegungdaten: Vorbelegungdaten,
  angebotsId: string,
): SagaIterator {
  const state = (yield select()) as IAppState;
  const tkvVorbelegungdaten: TkvV2Vorbelegungdaten = vorbelegungdaten as TkvV2Vorbelegungdaten;
  const globalPut = GlobalPut.getInstance();
  tkvVorbelegungdaten.angebotsId = angebotsId;
  tkvVorbelegungdaten.forApp = App.TKV_V2;
  const shouldContinue = yield* prefillProdukt(state, tkvVorbelegungdaten, angebotsId);
  if (!shouldContinue) {
    return;
  }
  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.TKV_V2,
        state.angebotsId,
      ),
    );
  }
  if (tkvVorbelegungdaten.zuschlagRisiko) {
    yield call(
      globalPut.put,
      new GlobalActions().zuschlagrisikoReceived({
        zuschlagRisiko: tkvVorbelegungdaten.zuschlagRisiko,
        appId: App.TKV_V2,
        angebotsId: state.angebotsId,
      }),
    );
  }
  yield put(tkvActions.produktZusatzOptionenVorbelegt(tkvVorbelegungdaten));
  yield* finishVorbelegung(state, tkvVorbelegungdaten, angebotsId);
  yield call([globalPut, globalPut.put], globalActions.vorbelegungFinished({ appId: App.TKV_V2 }));
}

export function* errorCallback(error: DoRequestResponse<AxiosError>): SagaIterator {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  if (state.angebotsId !== error.doRequestAction.payload.angebotsId) {
    return yield cancel();
  }
  yield call(
    globalPut.put,
    globalActions.vorbelegungFinished({
      appId: App.TKV_V2,
      angebotsId: state.angebotsId,
    }),
  );
  yield cancel();
}
